/**
 * Clerk OAuth callback handler — the equivalent of `AuthCallbackPage` for
 * Clerk SSO flows (Google / Apple).
 *
 * When the user starts an OAuth flow (`signUp.authenticateWithRedirect`), we
 * set `redirectUrl` to include `__clerk_sso_callback=1`.  `App.tsx` detects
 * this query param on mount and renders this page instead of the funnel.
 *
 * `AuthenticateWithRedirectCallback` (from `@clerk/clerk-react`) consumes
 * Clerk's internal state to complete the sign-up / sign-in, then redirects
 * to `redirectUrlComplete` (the clean funnel URL) where the active session
 * will be picked up by `ClerkEmailAuth`'s session effect.
 */
import { AuthenticateWithRedirectCallback } from "@clerk/clerk-react";

const CALLBACK_PARAM = "__clerk_sso_callback";

/**
 * Detect the Clerk SSO callback marker on the current URL.
 */
export const isClerkSsoCallbackUrl = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return params.get(CALLBACK_PARAM) === "1";
};

/**
 * Build the callback URL by appending the marker to the current location.
 * Used by `clerkEmailAuth` when initiating SSO.
 */
export const buildClerkSsoCallbackUrl = (): string => {
  const url = new URL(window.location.href);
  url.searchParams.set(CALLBACK_PARAM, "1");
  return url.pathname + url.search;
};

/**
 * Build the "complete" URL (the clean funnel URL without the callback marker).
 * This is where Clerk redirects after the OAuth flow completes.
 */
export const buildClerkSsoCompleteUrl = (): string => {
  const url = new URL(window.location.href);
  // Strip the custom callback marker and any Clerk-internal params
  // (e.g. __clerk_db_jwt, __clerk_status) that Clerk may append to
  // the redirect URL during the OAuth flow.
  for (const key of [...url.searchParams.keys()]) {
    if (key === CALLBACK_PARAM || key.startsWith("__clerk")) {
      url.searchParams.delete(key);
    }
  }
  return url.pathname + url.search;
};

export const ClerkSsoCallbackPage = () => {
  const completeUrl = buildClerkSsoCompleteUrl();
  return (
    <div className="flex h-dvh w-full items-center justify-center bg-[hsl(var(--brand-bg))] text-[hsl(var(--brand-bg-foreground))]">
      <div className="flex flex-col items-center gap-3">
        <div className="size-10 animate-spin rounded-full border-2 border-[hsl(var(--brand-bg-foreground)/0.2)] border-t-[hsl(var(--brand-bg-foreground))]" />
        <p className="text-sm opacity-80">Signing you in&hellip;</p>
      </div>
      <AuthenticateWithRedirectCallback
        signUpForceRedirectUrl={completeUrl}
        signInForceRedirectUrl={completeUrl}
      />
    </div>
  );
};

export default ClerkSsoCallbackPage;
